import React, { useCallback } from 'react';
import { useForm, Resolver, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { validate as validateUuid } from 'uuid';

import { CreateNonRegisteredBLProfile } from '../../../../connection/sessions';
import { setBlUser, toggleRegistrationModal } from '../../../../store/actions/event/event-actions';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { BlProfile, CreateNonRegistrationProfiles } from '../../../../types/working-model';
import { setStorageItem } from '../../../../utils/local-storage';

import ModalComponent from "../../../general-ui/modal/modal";

enum EInputNames {
	first_name = 'first_name',
	last_name = 'last_name',
	email = 'email'
}

interface IFormInputs {
	first_name: string;
	last_name: string;
	email: string;
}

interface IFormFieldProps {
	error: boolean;
	field: EInputNames;
	register: UseFormRegister<IFormInputs>;
	type?: string;
	[key: string]: any;
}

interface INonRegisteredUserFormProps {
	event: number;
	event_uuid: string;
	channel: number;
}


const handleErrors = (values: any) => {
	const errors: { [key: string]: any } = {};

	if (!values.first_name.trim().length) {
		errors.first_name = {
			type: 'required',
			message: ''
		};
	}
	if (!values.last_name.trim().length) {
		errors.last_name = {
			type: 'required',
			message: ''
		};
	}
	if (!values.email.trim().length) {
		errors.email = {
			type: 'required',
			message: ''
		};
	}

	return errors;
};

const resolver: Resolver<IFormInputs> = async (values) => {
	return {
		values: values,
		errors: handleErrors(values),
	};
};

const FormField: React.FC<IFormFieldProps> = ({ error, field, register, type = 'text', ...rest }) => {
	return (
		<div className="field-group text normal">
			<input
				type={type}
				className={`${error ? 'input-error' : ''}`}
				{...register(field)}
				{...rest}
			/>
		</div>
	);
};

const NonRegisteredUserForm: React.FC<INonRegisteredUserFormProps> = ({
	event,
	event_uuid,
	channel,
}) => {

	const { register, handleSubmit, formState: { errors } } = useForm<IFormInputs>({ resolver });

	const showRegistrationModal = useTypedSelector(state => state.LiveEventReducer.showRegistrationModal);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);

	const { t } = useTranslation("session");

	const dispatch = useDispatch();

	const onSubmit = handleSubmit(async data => {
		// submit info to api to register a new bl profile for non registered users
		try {

			const session_uuid = showRegistrationModal;

			if (!session_uuid || !validateUuid(session_uuid) || !validateUuid(event_uuid)) return;

			const session = eventBundle?.sessions?.find(session => session.uuid === session_uuid);

			if (!session) return;

			const profileData: CreateNonRegistrationProfiles = {
				...data,
				session: session.session,
				session_uuid,
				event,
				event_uuid,
				channel,
			};

			const { result, token } = await CreateNonRegisteredBLProfile(profileData);
			setStorageItem('Bl-Profile-Token', token);
			dispatch(setBlUser(result as BlProfile));
			dispatch(toggleRegistrationModal(null));
		} catch (e) {
			console.error(e?.response || e);
		}
	});

	const onClose = useCallback(() => {
		dispatch(toggleRegistrationModal(null));
	}, [dispatch]);

	return (
		<ModalComponent
			title={t("Profile details")}
			open={!!showRegistrationModal}
			onRequestClose={onClose}
			closeable={false}
			cancellable
		>
			<form onSubmit={onSubmit}>
				<FormField
					error={!!errors?.first_name}
					field={EInputNames.first_name}
					register={register}
					placeholder={t("First name")}
					autoComplete="given-name"
				/>
				<FormField
					error={!!errors?.last_name}
					field={EInputNames.last_name}
					register={register}
					placeholder={t("Last name")}
					autoComplete="family-name"
				/>
				<FormField
					error={!!errors?.email}
					field={EInputNames.email}
					register={register}
					placeholder={t("Email")}
					autoComplete="email"
					type="email"
				/>

				<div className="non-registered-modal-actions">
					<button type="button" onClick={onClose}>{t("Cancel")}</button>
					<button className="primary" type="submit">{t("Submit")}</button>
				</div>
			</form>
		</ModalComponent>
	);
};

export default NonRegisteredUserForm;
